import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { subscribe, getChallenge, finishChallenge } from '../security/twoFactorChallenge';

let loadedDialog = null;
let pendingDialog = null;
const loadDialog = () => {
  if (loadedDialog) return Promise.resolve(loadedDialog);
  if (!pendingDialog) pendingDialog = import('./TwoFactorChallengeDialog')
    .then(module => { loadedDialog = module.default; return loadedDialog; })
    .catch(error => { pendingDialog = null; throw error; });
  return pendingDialog;
};

export default function TwoFactorChallenge() {
  // Keep the subscriber eager: sign-in remains pending even while dialog assets load.
  const challenge = useSyncExternalStore(subscribe, getChallenge, () => null);
  const [Dialog, setDialog] = useState(() => loadedDialog);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const cancelButton = useRef(null);
  useEffect(() => {
    if (challenge && !Dialog) cancelButton.current?.focus();
  }, [challenge, Dialog, attempt]);
  useEffect(() => {
    if (!challenge || Dialog) return undefined;
    let active = true;
    setLoadError(false);
    loadDialog().then(Component => { if (active) setDialog(() => Component); })
      .catch(() => { if (active) setLoadError(true); });
    return () => { active = false; };
  }, [challenge, Dialog, attempt]);

  if (!challenge) return null;
  if (Dialog) return <Dialog key={challenge.challengeToken} challenge={challenge} />;
  const cancel = () => finishChallenge(challenge.challengeToken, null, new Error('Sign-in verification cancelled.'));
  return <div role="dialog" aria-modal="true" aria-labelledby="two-factor-loading-title"
    onKeyDown={event => {
      if (event.key === 'Escape') cancel();
      if (event.key === 'Tab') {
        const buttons = [...event.currentTarget.querySelectorAll('button')];
        const current = buttons.indexOf(document.activeElement);
        const next = (current + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length;
        event.preventDefault(); buttons[next]?.focus();
      }
    }}
    style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'grid', placeItems: 'center', padding: 24, background: 'rgb(0 0 0 / .45)' }}>
    <section style={{ width: '100%', maxWidth: 420, padding: 24, borderRadius: 12, background: 'var(--color-background-soft, #fff)', color: 'var(--color-foreground, #1f2937)' }}>
      <h2 id="two-factor-loading-title" style={{ margin: '0 0 12px', fontSize: 20 }}>Two-factor authentication</h2>
      <p role={loadError ? 'alert' : 'status'}>{loadError ? 'Unable to load verification. Retry or cancel sign-in.' : 'Loading verification…'}</p>
      <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
        {loadError && <button className="store-button" onClick={() => setAttempt(value => value + 1)}>Retry verification</button>}
        <button className="store-button" onClick={cancel} ref={cancelButton}>Cancel</button>
      </div>
    </section>
  </div>;
}
